import React, { useEffect, useState } from "react";
import { FilterForm } from "./FilterForm";
import { ProjectChargesCard } from "./ProjectChargesCard";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { VisibilityListener } from "~/visibility-listener";
import useInfiniteScroll from "~/components/search-page-wrapper/infinite-scroll/useInfiniteScroll";
import {
  Action,
  State,
} from "~/components/search-page-wrapper/infinite-scroll/stateMachine";
import _ from "lodash";
import { LoadUntil } from "~/scrollTo/LoadUntil";
import { ScrollToProvider } from "~/scrollTo";
import { ResetSearchItemsProvider } from "~/components/search-page-wrapper/infinite-scroll/ResetSearchItems";
import { ChargesReports } from "./ChargesReports";
import { SearchFilterContainer } from "~/components/search-filter-container";
import { ChargeBulkActions } from "~/visuals/organisms/ChargeBulkActions";
import { SearchStatsProvider, useSearchStats } from "./SearchStatsProvider";
import { SelectAllCheckbox } from "~/components/search-page-wrapper/BulkCheckboxes/SelectAllCheckbox";
import { LoadAllCharges } from "./LoadAllCharges";
import { Project, ProjectCharge, ProjectSortBy } from "~/gql/types";
import {
  ProjectChargesSearchWithStatsDocument,
  useProjectChargesSearchQuery,
  useProjectChargesSearchWithStatsQuery,
} from "./query.generated";
import { useApolloClient } from "@apollo/client";
import { SortCharges } from "./SortCharges";
import { ProjectCountReactor } from "./ProjectCountReactor";
import { ProjectPlaceholder } from "./ProjectPlaceholder";
import { ProjectChargesFilter } from "./types";
import { formattedDate } from "./utils";
import {
  SearchPageWrapper,
  SearchListContent,
} from "~/components/search-page-wrapper";
import {
  Outlet,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import styled from "@emotion/styled";
import {
  ChargeEditorHost,
  ChargeEditorHostContext,
} from "~/project-charge-form/ChargeEditorHost";
import { SingleChargeLoader } from "~/single-charge-loader";
import { projectCharge, projectCharges } from "~/routes";

const Breadcrumbs: React.FC = () => {
  useBreadcrumbs([{ text: "Project Charges" }], []);

  return <></>;
};

// One persistently-mounted wrapper whose `display` toggles between `contents`
// (index) and `none` (editor shown). Hiding, not unmounting, is what keeps the
// list's state and its `.content` scroll offset alive across an edit.
const ContentSubtree = styled.div<{ hidden: boolean }>`
  display: ${({ hidden }) => (hidden ? "none" : "contents")};
`;

type ProjectChargesPageProps = {
  FilterFormComponent?: typeof FilterForm;
  ScrollToProvider?: typeof ScrollToProvider;
};

export type ProjectChargesPageDisplayProps = {
  showSpinner: boolean;
  loadingMore: boolean;
  projectCharges: ProjectCharge[] | null;
  state: State<ProjectCharge, ProjectChargesFilter>;
  dispatch: React.Dispatch<Action<ProjectCharge, ProjectChargesFilter>>;
};

export const getOrderer = (sortBy: ProjectSortBy) => {
  if (sortBy === "ProjectId") {
    return [
      (x) => x.project!.number,
      (x) => x.project!.customer!.name.toLowerCase(),
    ];
  }

  if (sortBy === "ProjectName") {
    return [
      (x) => x.project!.name.toLowerCase(),
      (x) => x.project!.customer!.name.toLowerCase(),
    ];
  }

  return [
    (x) => x.project!.customer!.name.toLowerCase(),
    (x) => x.project!.number,
  ];
};

export const arrangeCharges = (
  charges: ProjectCharge[] | null,
  orderer: ((x: any) => any)[]
) => {
  const projects = _.chain(charges)
    .groupBy((x) => x.project!.number)
    .mapValues((vals) => ({
      project: vals[0].project,
      charges: vals,
    }))
    .values()
    .value();

  const sortedProjects = _.orderBy(projects, orderer);

  return sortedProjects;
};

export const ProjectChargesPageDisplay: React.FC<
  ProjectChargesPageDisplayProps
> = ({ showSpinner, loadingMore, projectCharges, state, dispatch }) => {
  const projects = arrangeCharges(
    projectCharges,
    getOrderer(state.filterOptions.sortBy)
  ) as { project: Project; charges: ProjectCharge[] }[];

  return (
    <SearchListContent
      loadingMore={loadingMore}
      loading={showSpinner}
      actions={
        <SelectAllCheckbox
          {...{ state, dispatch, checkboxMargin: "8px 0 0 13px" }}
        >
          <LoadAllCharges {...{ state, dispatch }} />
        </SelectAllCheckbox>
      }
    >
      <div className="project-charges-display">
        {projects &&
          projects.map((proj, idx) => (
            <ProjectChargesCard {...{ ...proj, state, dispatch }} key={idx} />
          ))}

        <ProjectPlaceholder {...{ state, dispatch }} />

        {state.showVisibility && (
          <VisibilityListener
            onShown={() => {
              dispatch({ tag: "ScrolledToBottom" });
            }}
            height={10}
          />
        )}
        <ChargeBulkActions
          {...{
            state,
            dispatch,
            loading: showSpinner || loadingMore,
          }}
        />
      </div>
    </SearchListContent>
  );
};

type LoadProjectChargesProps = {
  dispatch: React.Dispatch<Action<ProjectCharge, ProjectChargesFilter>>;
  tag: "DataLoaded" | "MoreDataLoaded";
  token: string | null;
  filters: ProjectChargesFilter;
};

const LoadProjectCharges = ({
  dispatch,
  tag,
  token,
  filters: {
    employee,
    billingAdmin,
    projectManager,
    projectSupervisor,
    searchText,
    project,
    customer,
    state,
    billable,
    dateAfter,
    dateBefore,
    dateEqual,
    afeWoPo,
    officeCode,
    sortBy,
  },
}: LoadProjectChargesProps) => {
  const { setStats } = useSearchStats();

  const queryHook = token
    ? useProjectChargesSearchQuery
    : useProjectChargesSearchWithStatsQuery;

  const variables = {
    token,
    employee: employee?.userPrincipalName ?? null,
    billingAdmin: billingAdmin?.userPrincipalName ?? null,
    projectSupervisor: projectSupervisor?.userPrincipalName ?? null,
    projectManager: projectManager?.userPrincipalName ?? null,
    searchText: searchText || null,
    projectNumber: project?.number || null,
    customerNumber: customer?.number || null,
    state: state || null,
    billable: billable || null,
    dateEqual: formattedDate(dateEqual),
    dateBefore: formattedDate(dateBefore),
    dateAfter: formattedDate(dateAfter),
    afeWoPo: afeWoPo || null,
    officeCode: officeCode?.officeCode ?? null,
    sortBy: sortBy as ProjectSortBy,
  };

  const { data } = queryHook({ variables });

  useEffect(() => {
    if (data) {
      const newToken = data?.projectCharges?.search?.token as string | null;
      const newProjectCharges = data?.projectCharges?.search
        ?.records as ProjectCharge[];
      const stats = data?.projectCharges?.["stats"];
      if (stats) {
        setStats(stats);
      }
      dispatch({ tag, items: newProjectCharges, searchToken: newToken });
    }
  }, [data]);

  return <></>;
};

export type LoadChargesUntilProps = {
  dispatch: React.Dispatch<Action<ProjectCharge, ProjectChargesFilter>>;
  filters: ProjectChargesFilter;
};

export const LoadChargesUntil = ({
  dispatch,
  filters: {
    employee,
    billingAdmin,
    projectManager,
    projectSupervisor,
    searchText,
    project,
    customer,
    state,
    billable,
    dateAfter,
    dateBefore,
    dateEqual,
    afeWoPo,
    sortBy,
    officeCode,
    projectNumber,
  },
}: LoadChargesUntilProps): JSX.Element => {
  const { setStats } = useSearchStats();

  const client = useApolloClient();
  const [searchParams] = useSearchParams();

  const variables = {
    employee: employee?.userPrincipalName ?? null,
    billingAdmin: billingAdmin?.userPrincipalName ?? null,
    projectSupervisor: projectSupervisor?.userPrincipalName ?? null,
    projectManager: projectManager?.userPrincipalName ?? null,
    searchText: searchText || null,
    projectNumber: project?.number || null,
    customerNumber: customer?.number || null,
    state: state || null,
    billable: billable || null,
    dateEqual: formattedDate(dateEqual),
    dateBefore: formattedDate(dateBefore),
    dateAfter: formattedDate(dateAfter),
    afeWoPo: afeWoPo || null,
    officeCode: officeCode?.officeCode ?? null,
    sortBy,
  };

  const getResults = async (token: string | null) => {
    const { data } = await client.query({
      query: ProjectChargesSearchWithStatsDocument,
      variables: { ...variables, token: token || null },
    });
    const newToken = data?.projectCharges?.search?.token as string | null;
    const newItems = data?.projectCharges?.search?.records as any[];
    const stats = data?.projectCharges?.stats;
    if (stats) {
      setStats(stats);
    }

    return { newToken, newItems };
  };

  const getCountExceeded = (items: ProjectCharge[]): boolean => {
    const projectCount = searchParams.get("projectCount");

    const countExceeded =
      projectCount !== null &&
      projectCount !== "" &&
      new Set(items.map((x) => x.projectId)).size > +projectCount * 1.01;

    return countExceeded;
  };

  const getIsLoaded = (items: ProjectCharge[]): boolean => {
    return (
      items.some((x) => x.projectId === +projectNumber!) ||
      getCountExceeded(items)
    );
  };

  return (
    <LoadUntil
      {...{
        dispatch,
        currentFilter: projectNumber!,
        getIsLoaded,
        getResults,
        getCountExceeded,
      }}
    />
  );
};

export const ProjectChargesPage: React.FC<ProjectChargesPageProps> = (
  props
) => {
  return (
    <SearchStatsProvider>
      <ProjectChargesPageInner {...props} />
    </SearchStatsProvider>
  );
};

const ProjectChargesPageInner: React.FC<ProjectChargesPageProps> = (props) => {
  const [filters, setFilters] = useState<ProjectChargesFilter | null>(null);
  const FilterFormComponent = props.FilterFormComponent || FilterForm;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Hide, never unmount. `edit/:id` is a child route rendered through the
  // <Outlet/> below; while it matches, the content subtree is display:none (not
  // conditionally removed), so its state and .content scroll offset survive.
  const editing = useMatch(projectCharge.path);

  const setFilterOptions = (opts: ProjectChargesFilter) => setFilters(opts);

  // dismiss drops /edit/:id and keeps ?filters. useCallback so it is a stable
  // member of the memoized host (correctness is explicit, not resting on
  // `navigate` happening to be stable).
  const dismiss = React.useCallback(
    () =>
      navigate({
        pathname: projectCharges.path,
        search: searchParams.toString(),
      }),
    [navigate, searchParams]
  );

  // The host contract is held at page level so it wraps both the (always
  // mounted) content subtree and the <Outlet/>. Its resolvable data — the load
  // status and the current item set — is supplied from below by
  // ProjectChargesResults (which owns the infinite-scroll reducer, seeded with
  // the real filters). Until that reports, the host is `loading` so the outlet
  // shows a spinner rather than bouncing.
  //
  // The published *data* changes on every page load / scroll, but the `resolve`
  // and `absorbSaved` *callbacks* stay stable: `resolve` reads the latest items
  // through a ref, `absorbSaved` is the constant `setAbsorbId`. So the host
  // value only re-memoizes when data that actually affects resolution changes,
  // not on every reducer tick — keeping the seam cheap for hosts that keep the
  // editor mounted.
  const [hostData, setHostData] = useState<{
    status: "loading" | "ready";
    items: ProjectCharge[];
  }>({ status: "loading", items: [] });
  const [absorbId, setAbsorbId] = useState<number | null>(null);

  const itemsRef = React.useRef<ProjectCharge[]>(hostData.items);
  itemsRef.current = hostData.items;

  const resolve = React.useCallback(
    (id: number) => itemsRef.current.find((x) => x.id === id),
    []
  );
  const absorbSaved = React.useCallback((id: number) => setAbsorbId(id), []);

  const host = React.useMemo<ChargeEditorHost>(
    () => ({ status: hostData.status, resolve, dismiss, absorbSaved }),
    [hostData.status, resolve, dismiss, absorbSaved]
  );

  return (
    <ChargeEditorHostContext.Provider value={host}>
      {/* Index breadcrumb only when not editing — while the editor is shown it
          owns the breadcrumb trail; the hidden content subtree must not also
          write breadcrumbs. */}
      {!editing && <Breadcrumbs />}
      <ContentSubtree hidden={editing !== null}>
        <SearchPageWrapper withSort withPadding>
          <SearchFilterContainer>
            <FilterFormComponent onFiltersChanged={setFilterOptions} />
            <SortCharges
              {...{
                filters,
                setFilters,
                searchParams,
                setSearchParams,
              }}
            />
            <ChargesReports {...{ filters }} />
          </SearchFilterContainer>
          {/* The reducer owner mounts once filters exist (seeded with the real
              filters, as the old ProjectChargesResults was); the filter bar
              above stays mounted across that transition. */}
          {filters && (
            <ProjectChargesResults
              {...{ ...props, filters, setHostData, absorbId, setAbsorbId }}
            />
          )}
        </SearchPageWrapper>
      </ContentSubtree>
      <Outlet />
    </ChargeEditorHostContext.Provider>
  );
};

type ProjectChargesResultsProps = ProjectChargesPageProps & {
  filters: ProjectChargesFilter;
  setHostData: React.Dispatch<
    React.SetStateAction<{
      status: "loading" | "ready";
      items: ProjectCharge[];
    }>
  >;
  absorbId: number | null;
  setAbsorbId: React.Dispatch<React.SetStateAction<number | null>>;
};

const ProjectChargesResults: React.FC<ProjectChargesResultsProps> = (props) => {
  const [searchParams] = useSearchParams();
  const { filters, setHostData, absorbId, setAbsorbId } = props;
  const { setState } = useSearchStats();

  // Seeded with the real filters (this component mounts only once filters exist,
  // exactly as the old owner did — the Loading state ignores
  // SearchCriteriaChanged, so the seed is what the first search reads).
  const initialState = {
    tag: "Loading",
    items: [],
    showVisibility: false,
    loading: true,
    filterOptions: filters,
    searchToken: null,
    selectIdKey: "id",
    selectedItems: [],
  } as State<ProjectCharge, ProjectChargesFilter>;

  const { state, dispatch } = useInfiniteScroll<
    ProjectCharge,
    ProjectChargesFilter
  >(initialState);

  useEffect(() => {
    dispatch({ tag: "SearchCriteriaChanged", filterOptions: filters });
  }, [filters]);

  useEffect(() => {
    setState(state);
  }, [state.items]);

  // Publish the resolvable data (status + items) up to the page-level host. The
  // page builds the stable `resolve`/`absorbSaved` callbacks over this data, so
  // only the data — not the callback identities — changes on a reducer tick.
  useEffect(() => {
    setHostData({
      status: state.tag === "Loading" ? "loading" : "ready",
      items: state.items,
    });
  }, [state.items, state.tag]);

  const defaultLoadProps = {
    filters: state.filterOptions,
    dispatch,
  };

  const ScrollProvider = props.ScrollToProvider || ScrollToProvider;

  return (
    <ResetSearchItemsProvider
      reset={() =>
        dispatch({
          tag: "Reset",
          filterOptions: {
            ...state.filterOptions,
            projectNumber: searchParams.get("lastProject"),
          },
        })
      }
    >
      <ScrollProvider>
        {state.tag === "Loading" &&
          (state.filterOptions.projectNumber && !state.items?.length ? (
            <LoadChargesUntil
              {...{
                ...defaultLoadProps,
              }}
            />
          ) : (
            <LoadProjectCharges
              {...{
                tag: "DataLoaded",
                token: null,
                ...defaultLoadProps,
              }}
            />
          ))}

        {state.tag === "LoadingMore" && (
          <LoadProjectCharges
            {...{
              tag: "MoreDataLoaded",
              token: state.searchToken,
              ...defaultLoadProps,
            }}
          />
        )}

        <ProjectChargesPageDisplay
          {...{
            projectCharges: state.items,
            loadingMore: state.tag === "LoadingMore",
            showSpinner: state.tag === "Loading",
            state,
            dispatch,
          }}
        />
        <ProjectCountReactor {...{ state }} />
        {/* Write-back lives here with the reducer (needs `dispatch`) and keeps
            running while the content subtree is hidden, so the row is patched
            before the editor dismisses. `absorbId` is page-owned so the host's
            `absorbSaved` can trigger it. */}
        <SingleChargeLoader
          {...{
            dependency: absorbId !== null,
            id: absorbId ?? 0,
            callback: (charge: ProjectCharge) => {
              dispatch({ tag: "UpdateItem", item: charge });
              setAbsorbId(null);
            },
            // A failed re-read must clear the trigger, or `absorbId` sticks
            // non-null and every later absorb is dead. The row keeps its last
            // saved value (canonical state just didn't refresh) rather than
            // being stranded silently.
            onError: () => setAbsorbId(null),
          }}
        />
      </ScrollProvider>
    </ResetSearchItemsProvider>
  );
};
